// Pure decision logic for PlanCtaCard — extracted so the surface choice and
// the returning-user "text UPDATE to refresh" hint can be unit-tested without
// a DOM. The component renders against this; it holds no JSX so it runs under
// the server's node-only vitest.

export interface PlanCtaInput {
  country: string | null
  handoffToken: string | null
  isMinor?: boolean
  hasExistingHandoff?: boolean
  // ?sms=1 debug affordance: forces the US SMS branch from a non-US IP. The
  // component reads window.location; tests pass it explicitly.
  forceSms?: boolean
}

export type PlanCtaSurface = 'none' | 'us_sms' | 'intl_email'

export interface PlanCtaDecision {
  surface: PlanCtaSurface
  // Only ever true on the US SMS surface, and only for a returning user who
  // already clicked the SMS claim link on a prior calc (hasExistingHandoff).
  // First-time users never see the UPDATE hint.
  showRehandoffHint: boolean
}

export function decidePlanCtaSurface(input: PlanCtaInput): PlanCtaDecision {
  const { country, handoffToken, isMinor, hasExistingHandoff, forceSms } = input

  // Under-18 gate: never any conversion surface for a minor.
  if (isMinor) return { surface: 'none', showRehandoffHint: false }

  const isUs = (country === 'US' || forceSms === true) && !!handoffToken

  if (isUs && handoffToken) {
    return {
      surface: 'us_sms',
      // Returning-user hint is US-only and requires a confirmed prior SMS
      // link click. Gated to true ONLY here, so first-timers and non-US users
      // can never see it.
      showRehandoffHint: hasExistingHandoff === true,
    }
  }

  // Non-US (or US without a token): email capture control. No UPDATE hint.
  return { surface: 'intl_email', showRehandoffHint: false }
}

// wa.me deep-link builder for the WhatsApp CTA. Dark-shipped: the number is
// baked in from VITE_WHATSAPP_NUMBER at build time and is unset in dev/tests, so
// this returns null and BOTH surfaces render their legacy control UNCHANGED (US
// SMS card, intl email form). Gating, spelled out so the quadrants are
// unit-testable without a DOM:
//   * A non-null return is the "live design is on" signal for BOTH conversion
//     surfaces (us_sms + intl_email), but each USES it differently: intl makes
//     WhatsApp the sole CTA, while US demotes it to a secondary text link under
//     the primary SMS button (2026-07-30 flip — a WhatsApp-primary US card
//     halved paid click-through 45% → 20%, so SMS leads again there; WhatsApp
//     stays primary on intl, where the 915 US SMS number is unavailable). Only
//     the minor/none gate is excluded (no conversion surface at all).
//   * No handoff token (no plan to claim) → null → control surface.
//   * No configured number → null → control surface (the dark-ship switch).
// The number is normalized to bare digits (wa.me wants E.164 without '+' or
// separators). The prefill is the SAME first-person sentence the SMS CTA uses
// (t.cta.smsPrefill, token lowercased) — passed in by the component so this
// stays i18n-agnostic. The all-caps `START <token>` grammar was removed: it
// read as carrier-spam and was a measured conversion killer (2026-06-02); the
// WhatsApp channel parses the slug from anywhere in the sentence, so no server
// change is needed.
export function planWhatsAppHref(input: {
  surface: PlanCtaSurface
  whatsappNumber: string | null | undefined
  handoffToken: string | null
  prefill: string
}): string | null {
  if (input.surface !== 'intl_email' && input.surface !== 'us_sms') return null
  if (!input.handoffToken) return null
  const digits = (input.whatsappNumber ?? '').replace(/\D/g, '')
  if (!digits) return null
  return `https://wa.me/${digits}?text=${encodeURIComponent(input.prefill)}`
}

// "WhatsApp is live" switch: true when VITE_WHATSAPP_NUMBER is baked in with at
// least one digit — independent of whether a given session has a claimable
// handoff token. Uses the SAME digit rule as planWhatsAppHref so the two never
// disagree. Gates the email-less live design vs the legacy dark-ship control:
// when configured, the plan CTA never renders an email surface (US = SMS button
// primary + WhatsApp secondary text link, intl = WhatsApp-only, nothing when
// there's no token); when unset, both surfaces fall back to today's control (US
// SMS card + email toggle, intl email form) so the dark-ship invariant +
// existing tests hold.
export function whatsAppConfigured(number: string | null | undefined): boolean {
  return (number ?? '').replace(/\D/g, '').length > 0
}

// Can this device actually COMPLETE a `wa.me` / `sms:` handoff?
//
// Both links need a phone. On a laptop, `sms:` has no handler at all and
// `wa.me` lands on WhatsApp Web, which itself demands you pick up your phone
// and scan a QR before it will send anything. The user is asked to do the very
// thing the link was supposed to save them.
//
// This is not a hypothesis. Measured 2026-07-25 over both CTA eras (US,
// dogfood excluded): desktop 56 impressions -> 6 clicks -> **0 redemptions**,
// versus mobile 419 -> 159 -> 74. Not one desktop visitor has EVER completed
// the handoff. Desktop is ~12% of US CTA impressions, structurally worth zero.
// So desktop gets a QR instead of a button (see WhatsAppQr) — the one path that
// moves the request onto the device that can finish it.
//
// Pure so it can be unit-tested without a DOM; the component passes
// navigator.userAgent / navigator.maxTouchPoints.
//
// FAILS SAFE TO PHONE: anything unrecognized returns true, i.e. the existing
// button surface. A wrong "phone" guess costs nothing (that is today's
// behaviour); a wrong "desktop" guess would replace a working button with a QR
// the user cannot scan because the phone IS the screen they are holding.
export function isPhoneLikeUserAgent(
  ua: string | null | undefined,
  maxTouchPoints?: number | null,
): boolean {
  const s = (ua ?? '').trim()
  if (!s) return true // unknown → keep the button

  // Positive phone signals first.
  if (/Android|iPhone|iPod|Windows Phone|IEMobile|Mobile Safari|\bMobi\b/i.test(s)) return true
  if (/\biPad\b/i.test(s)) return true
  // iPadOS 13+ (and "Request Desktop Site") report a Macintosh UA with NO
  // mobile token; touch points are the only thing separating an iPad from a
  // MacBook. A Mac trackpad reports 0.
  if (/Macintosh/i.test(s) && (maxTouchPoints ?? 0) > 1) return true

  // Then, and only then, KNOWN desktop platforms. Note this is deliberately an
  // allow-list of desktops rather than "everything left over is a desktop":
  // an unrecognized UA falls through to the phone default below. Touch is NOT
  // consulted here — a touchscreen Windows laptop still has no SMS app and no
  // WhatsApp deep-link handler, so it needs the QR exactly like any laptop.
  if (/Macintosh|Windows NT|\bX11\b|\bCrOS\b|Linux x86_64/i.test(s)) return false

  // Anything else → phone. The asymmetry is the whole point: guessing "phone"
  // wrongly just leaves today's button in place (a desktop that converts at 0%
  // either way), while guessing "desktop" wrongly hands a QR to someone whose
  // phone IS the screen they are holding, breaking a path that worked.
  return true
}

// Click-beacon URL builder for POST /handoff/:token/click. Pure so the
// channel-routing (SMS default vs WhatsApp) is unit-testable without a DOM /
// navigator. Default (no channel) omits the query param entirely so the SMS
// path hits the endpoint byte-identically to before; 'whatsapp' appends
// ?channel=whatsapp so the server stamps wa_link_clicked_at (0016).
export function planClickBeaconUrl(input: {
  apiBase: string
  handoffToken: string
  channel?: 'whatsapp'
}): string {
  const base = `${input.apiBase}/api/handoff/${encodeURIComponent(input.handoffToken)}/click`
  return input.channel ? `${base}?channel=${input.channel}` : base
}
